import type { TrendOverlay, TrendPoint, TrendSeries } from '@bcecloud/portal-ui';
import { getJson } from './api';
import { STATS_INTERVAL, type StatsInterval } from './leads';
import { DAY_MS, RANGES, periodLabel, rangeWindow, type RangeKey } from './report-window';
import { compactMoney } from './sales';

/**
 * Analytics → Sales: typed fetcher over /api/v3/sales/analytics and the pure
 * helpers that turn it into the range presets, the chart (per period or
 * cumulative, with trailing 3/6/12-month lines and last year) and the
 * group-by table. Shapes mirror the API's sales-analytics.ts; every
 * definition is the scorecard's.
 */

/** What the chart splits and the table groups deals by (mirrors the API's SALES_GROUP). */
export const SALES_GROUP = {
  rep: 'rep',
  channel: 'channel',
  program: 'program',
  customer: 'customer',
} as const;
export type SalesGroup = (typeof SALES_GROUP)[keyof typeof SALES_GROUP];

export const SALES_GROUP_LABEL: Readonly<Record<SalesGroup, string>> = {
  [SALES_GROUP.rep]: 'Rep',
  [SALES_GROUP.channel]: 'Channel',
  [SALES_GROUP.program]: 'Program',
  [SALES_GROUP.customer]: 'New vs repeat',
};

/** The chart unsplit (the API's `split=none`). */
export const NO_SPLIT = 'none';
export type SalesSplit = SalesGroup | typeof NO_SPLIT;

/** The trailing windows the chart can draw (mirrors the API's ROLLING_MONTHS). */
export const ROLLING_MONTHS = [3, 6, 12] as const;
export type RollingMonths = (typeof ROLLING_MONTHS)[number];

export interface SalesBucket {
  readonly booked: number;
  readonly bookings: number;
  readonly won: number;
  readonly lost: number;
  readonly created: number;
}

export interface RollingPoint extends SalesBucket {
  readonly days: number;
  readonly lastYear: SalesBucket | null;
}

export interface SalesGroupRow {
  readonly key: string;
  readonly ownerId: string | null;
  readonly bookings: number;
  readonly booked: number;
  readonly won: number;
  readonly lost: number;
  readonly winRate: number | null;
  readonly averageDeal: number | null;
  readonly medianDaysToSign: number | null;
  readonly open: number;
  readonly pipeline: number;
}

export interface SalesSummary {
  readonly from: string;
  readonly to: string;
  readonly booked: number;
  readonly bookings: number;
  readonly lost: number;
  readonly created: number;
  readonly winRate: number | null;
  readonly averageDeal: number | null;
  readonly medianDaysToSign: number | null;
}

export interface YoyPeriod {
  readonly key: 'mtd' | 'qtd' | 'ytd' | 't3m' | 't6m' | 't12m';
  readonly label: string;
  readonly from: string;
  readonly to: string;
  readonly booked: number;
  readonly bookings: number;
  readonly lastYearBooked: number;
  readonly lastYearBookings: number;
}

export interface CoveragePeriod {
  readonly key: 'quarter' | 'year';
  readonly label: string;
  readonly to: string;
  readonly goal: number;
  readonly booked: number;
  readonly goalRemaining: number;
  readonly pipeline: number;
  readonly deals: number;
  readonly ratio: number | null;
  readonly daysLeft: number;
  readonly neededPerDay: number;
}

export interface SalesAnalytics {
  readonly range: { readonly from: string; readonly to: string; readonly interval: StatsInterval };
  readonly series: ReadonlyArray<SalesBucket & { readonly period: string; readonly days: number }>;
  readonly lastYear: readonly SalesBucket[];
  readonly rolling: Readonly<Record<`${RollingMonths}`, ReadonlyArray<RollingPoint | null>>>;
  readonly summary: { readonly current: SalesSummary; readonly lastYear: SalesSummary };
  readonly yoy: readonly YoyPeriod[];
  readonly open: { readonly deals: number; readonly pipeline: number };
  /** Open deals now × the range's win rate × average deal ÷ days to sign, dollars a day. */
  readonly velocityPerDay: number | null;
  readonly coverage: readonly CoveragePeriod[];
  readonly goalPerDay: number;
  readonly split: SalesGroup | null;
  readonly segments: ReadonlyArray<{ readonly key: string; readonly buckets: readonly SalesBucket[] }> | null;
  readonly groupBy: SalesGroup;
  readonly groups: readonly SalesGroupRow[];
}

export interface SalesAnalyticsParams {
  /** Both days inclusive. */
  readonly from: string;
  readonly to: string;
  readonly interval: StatsInterval;
  readonly split: SalesSplit;
  readonly groupBy: SalesGroup;
}

export const SALES_ANALYTICS_QUERY_KEY = {
  root: ['sales-analytics'] as const,
  params: (params: SalesAnalyticsParams) => ['sales-analytics', params] as const,
};

export function readSalesAnalytics(params: SalesAnalyticsParams, signal?: AbortSignal): Promise<SalesAnalytics> {
  return getJson<SalesAnalytics>('/sales/analytics', { ...params }, signal);
}

// ─── Range presets ─────────────────────────────────────────────────────────

/** Calendar-to-date presets, beside the shared rolling ones. */
export const TO_DATE_RANGE = {
  mtd: { key: 'mtd', label: 'Month to date', shortLabel: 'MTD', interval: STATS_INTERVAL.day },
  qtd: { key: 'qtd', label: 'Quarter to date', shortLabel: 'QTD', interval: STATS_INTERVAL.week },
  ytd: { key: 'ytd', label: 'Year to date', shortLabel: 'YTD', interval: STATS_INTERVAL.month },
} as const;
export type ToDateRangeKey = keyof typeof TO_DATE_RANGE;
export type SalesRangeKey = ToDateRangeKey | RangeKey;

export const SALES_RANGE_OPTIONS: ReadonlyArray<{ readonly key: SalesRangeKey; readonly label: string; readonly shortLabel: string }> = [
  ...Object.values(TO_DATE_RANGE),
  ...Object.values(RANGES),
];

const MONTHS_PER_QUARTER = 3;

function isToDate(range: SalesRangeKey): range is ToDateRangeKey {
  return range in TO_DATE_RANGE;
}

/** The interval a range opens on until someone picks another. */
export function rangeInterval(range: SalesRangeKey): StatsInterval {
  return isToDate(range) ? TO_DATE_RANGE[range].interval : RANGES[range].interval;
}

/** A range as whole Eastern days, both inclusive, ending today. */
export function salesRangeWindow(range: SalesRangeKey, now: Date = new Date()): { from: string; to: string } {
  // The shared windows end tomorrow (exclusive); the analytics read wants today inclusive.
  const tomorrow = rangeWindow('7d', now).to;
  const today = new Date(Date.parse(`${tomorrow}T00:00:00.000Z`) - DAY_MS).toISOString().slice(0, 10);
  if (!isToDate(range)) return { from: rangeWindow(range, now).from, to: today };
  const year = today.slice(0, 4);
  const month = Number(today.slice(5, 7));
  const quarterStart = String(Math.floor((month - 1) / MONTHS_PER_QUARTER) * MONTHS_PER_QUARTER + 1).padStart(2, '0');
  const from = { mtd: `${today.slice(0, 7)}-01`, qtd: `${year}-${quarterStart}-01`, ytd: `${year}-01-01` }[range];
  return { from, to: today };
}

// ─── Chart ─────────────────────────────────────────────────────────────────

/** What the chart plots. */
export const SALES_METRIC = {
  booked: 'booked',
  bookings: 'bookings',
  winRate: 'winRate',
  averageDeal: 'averageDeal',
  created: 'created',
} as const;
export type SalesMetric = (typeof SALES_METRIC)[keyof typeof SALES_METRIC];

/** Per bucket, or a running total from the range's first day. */
export const CHART_MODE = { periodic: 'periodic', cumulative: 'cumulative' } as const;
export type ChartMode = (typeof CHART_MODE)[keyof typeof CHART_MODE];

const PERCENT = 100;

interface MetricSpec {
  readonly label: string;
  readonly unit: string;
  /** A ratio cannot stack or run cumulatively; its trend line is the window's own ratio. */
  readonly ratio: boolean;
  readonly value: (bucket: SalesBucket) => number;
  readonly format: (value: number) => string;
}

const count = (value: number): string => Math.round(value).toLocaleString('en-US');

export const SALES_METRIC_SPEC: Readonly<Record<SalesMetric, MetricSpec>> = {
  [SALES_METRIC.booked]: { label: 'Booked $', unit: 'booked', ratio: false, value: (bucket) => bucket.booked, format: compactMoney },
  [SALES_METRIC.bookings]: { label: 'Deals won', unit: 'deals won', ratio: false, value: (bucket) => bucket.bookings, format: count },
  [SALES_METRIC.winRate]: {
    label: 'Win rate',
    unit: 'win rate',
    ratio: true,
    value: (bucket) => (bucket.won + bucket.lost > 0 ? Math.round((PERCENT * bucket.won) / (bucket.won + bucket.lost)) : 0),
    format: (value) => `${Math.round(value)}%`,
  },
  [SALES_METRIC.averageDeal]: {
    label: 'Average deal',
    unit: 'average deal',
    ratio: true,
    value: (bucket) => (bucket.bookings > 0 ? bucket.booked / bucket.bookings : 0),
    format: compactMoney,
  },
  [SALES_METRIC.created]: { label: 'Deals created', unit: 'deals created', ratio: false, value: (bucket) => bucket.created, format: count },
};

export const ROLLING_LABEL: Readonly<Record<RollingMonths, string>> = { 3: '3-month trend', 6: '6-month trend', 12: '12-month trend' };
/** Short legend names: a split chart's legend stays one row on a desktop width (the chart never wraps it there). */
const ROLLING_LEGEND: Readonly<Record<RollingMonths, string>> = { 3: '3-mo trend', 6: '6-mo trend', 12: '12-mo trend' };
const LAST_YEAR_LEGEND = 'Last year';
/** Overlay strokes: the windows are ink, teal and purple; last year is the dashed grey everywhere else uses. */
const ROLLING_TONE: Readonly<Record<RollingMonths, TrendOverlay['tone']>> = { 3: 0, 6: 2, 12: 3 };
const LAST_YEAR_TONE = 1;
const GOAL_TONE = 2;

/** Signed percent change, or null without a base. */
export function changePct(current: number, previous: number): number | null {
  return previous === 0 ? null : ((current - previous) / previous) * PERCENT;
}

export function signedPct(pct: number | null): string {
  if (pct === null) return 'no base';
  const rounded = Math.round(pct);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

function runningTotal(values: readonly number[]): number[] {
  let sum = 0;
  return values.map((value) => (sum += value));
}

/**
 * A trailing window as a line on the bars' axis: a sum rescaled to the
 * bucket's days (so a daily bar and a 3-month trend compare), a ratio as the
 * window's own ratio.
 */
export function rollingValue(spec: MetricSpec, window: RollingPoint | null, bucketDays: number): number | null {
  if (window === null || window.days === 0) return null;
  return spec.ratio ? spec.value(window) : (spec.value(window) * bucketDays) / window.days;
}

export interface SalesChartOptions {
  readonly mode: ChartMode;
  readonly rolling: ReadonlySet<RollingMonths>;
  readonly lastYear: boolean;
  /**
   * The trend lines alone: last year is then the longest picked trend as it
   * stood a year earlier, not last year's raw buckets, so the two trends compare.
   */
  readonly trendsOnly?: boolean;
}

export interface SalesChart {
  readonly points: TrendPoint[];
  readonly series: TrendSeries[] | undefined;
  readonly overlays: TrendOverlay[];
}

/** The chart's points, split series and overlays for a metric. */
export function salesChart(data: SalesAnalytics, metric: SalesMetric, options: SalesChartOptions): SalesChart {
  const spec = SALES_METRIC_SPEC[metric];
  const cumulative = options.mode === CHART_MODE.cumulative && !spec.ratio;
  const values = data.series.map(spec.value);
  const shown = cumulative ? runningTotal(values) : values;
  const windows = cumulative ? [] : ROLLING_MONTHS.filter((months) => options.rolling.has(months));

  const points = data.series.map((bucket, position) => {
    const details: string[] = [];
    if (metric === SALES_METRIC.winRate) details.push(`${bucket.won} won of ${bucket.won + bucket.lost} decided`);
    else if (bucket.bookings > 0) details.push(`${bucket.bookings} won · ${compactMoney(bucket.booked)}`);
    for (const months of windows) {
      const window = data.rolling[`${months}`][position];
      if (!window) continue;
      const total = spec.value(window);
      const yearAgo = window.lastYear ? spec.value(window.lastYear) : null;
      const versus = yearAgo === null ? '' : ` (${signedPct(changePct(total, yearAgo))} on the year before)`;
      details.push(`Last ${months} months: ${spec.format(total)}${versus}`);
    }
    return { key: bucket.period, label: periodLabel(bucket.period, data.range.interval), value: shown[position] ?? 0, details };
  });

  const series = data.segments?.map((segment) => {
    const segmentValues = segment.buckets.map(spec.value);
    return { key: segment.key, label: segment.key, values: cumulative ? runningTotal(segmentValues) : segmentValues };
  });

  const overlays: TrendOverlay[] = windows.map((months) => ({
    key: `rolling-${months}`,
    label: ROLLING_LEGEND[months],
    values: data.series.map((bucket, position) => rollingValue(spec, data.rolling[`${months}`][position] ?? null, bucket.days)),
    tone: ROLLING_TONE[months],
  }));
  const longest = windows.at(-1);
  if (options.lastYear && options.trendsOnly && longest !== undefined) {
    overlays.push({
      key: 'last-year',
      label: `${ROLLING_LEGEND[longest]}, a year earlier`,
      values: data.series.map((bucket, position) => {
        const window = data.rolling[`${longest}`][position];
        return window?.lastYear ? rollingValue(spec, { ...window.lastYear, days: window.days, lastYear: null }, bucket.days) : null;
      }),
      tone: LAST_YEAR_TONE,
    });
  } else if (options.lastYear) {
    const yearAgo = data.lastYear.map(spec.value);
    overlays.push({ key: 'last-year', label: LAST_YEAR_LEGEND, values: cumulative ? runningTotal(yearAgo) : yearAgo, tone: LAST_YEAR_TONE });
  }
  if (cumulative && metric === SALES_METRIC.booked) {
    overlays.push({ key: 'goal', label: '$10M-a-year pace', values: runningTotal(data.series.map((bucket) => bucket.days * data.goalPerDay)), tone: GOAL_TONE });
  }
  return { points, series, overlays };
}

// ─── Table ─────────────────────────────────────────────────────────────────

/** A group row with its share of booked value, flattened for sorting. */
export interface SalesTableRow extends SalesGroupRow {
  readonly share: number | null;
}

export function salesTableRows(data: SalesAnalytics): SalesTableRow[] {
  const total = data.groups.reduce((sum, group) => sum + group.booked, 0);
  return data.groups.map((group) => ({ ...group, share: total > 0 ? group.booked / total : null }));
}
